import { defineStore } from 'pinia';
import { reactive, computed } from 'vue';
import { RouteLocationNormalizedLoaded as Route } from 'vue-router';
import { SitemapNode } from '@/plugins/sitemap';
import { NavigationHistory, BreadcrumbEntry } from '@/helpers/NavigationHistory';
import { useNavigationStore } from './navigation';

interface BreadcrumbState {
    breadcrumbs: BreadcrumbEntry[];
    extraCrumb: string;
    isLoading: boolean;
}

export const useBreadcrumbStore = defineStore('breadcrumbs', () =>
{
    const state = reactive<BreadcrumbState>({
        breadcrumbs: [],
        extraCrumb: '',
        isLoading: false
    });

    const navigationHistory = new NavigationHistory();

    // Computed properties
    const allCrumbs = computed(() =>
    {
        // If we have an extraCrumb, we need to replace the last breadcrumb with it
        if (state.extraCrumb && state.breadcrumbs.length > 0)
        {
            const breadcrumbs = [...state.breadcrumbs];
            const lastIndex = breadcrumbs.length - 1;

            // Create a new breadcrumb entry for the extraCrumb
            const extraNode: SitemapNode = {
                name: state.extraCrumb,
                visible: true,
                allowed: true,
                readOnly: false
            };

            // Replace the last breadcrumb with the extraCrumb
            breadcrumbs[lastIndex] = {
                node: extraNode,
                route: breadcrumbs[lastIndex]?.route || null,
                isCurrent: true
            };

            return breadcrumbs;
        }

        return state.breadcrumbs;
    });

    const lastCrumb = computed(() =>
    {
        if (state.extraCrumb)
        {
            const extraNode: SitemapNode = {
                name: state.extraCrumb,
                visible: true,
                allowed: true,
                readOnly: false
            };

            return {
                node: extraNode,
                route: null, // We don't have a route for extra crumbs
                isCurrent: true
            };
        }

        // Find the current breadcrumb (marked as isCurrent: true)
        const currentBreadcrumb = state.breadcrumbs.find(b => b.isCurrent);

        return currentBreadcrumb || state.breadcrumbs[state.breadcrumbs.length - 1] || null;
    });

    // Actions
    const updateBreadcrumbs = async (route: Route, sitemapNode: SitemapNode) =>
    {
        state.isLoading = true;

        try
        {
            // Check if this is sitemap navigation from the navigation store
            const navigationStore = useNavigationStore();
            const isSitemapNavigation = navigationStore.isSitemapNavigation;

            // Add entry to navigation history
            navigationHistory.addEntry(route, sitemapNode, isSitemapNavigation);

            // Clear the sitemap navigation flag after using it
            navigationStore.clearSitemapNavigation();

            // Get breadcrumbs from navigation history
            state.breadcrumbs = navigationHistory.getBreadcrumbs(route, sitemapNode);

            /*
            if (import.meta.env.DEV)
            {
                navigationHistory.debugState(route, sitemapNode);
            }
            */
        }
        catch (error)
        {
            // Fallback to sitemap hierarchy
            state.breadcrumbs = [{
                node: sitemapNode,
                route,
                isCurrent: true
            }];
        }
        finally
        {
            state.isLoading = false;
        }
    };

    const setExtraCrumb = (extraCrumb: string) =>
    {
        state.extraCrumb = extraCrumb;
    };

    const clearExtraCrumb = () =>
    {
        state.extraCrumb = '';
    };

    const clearHistory = () =>
    {
        navigationHistory.clearHistory();
        state.breadcrumbs = [];
    };

    const getBreadcrumbUrl = (entry: BreadcrumbEntry, currentRoute: Route) =>
    {
        // This should be implemented based on your sitemap URL generation logic
        // For now, we'll return the route from the entry
        return entry.route;
    };

    return {
        // State
        breadcrumbs: computed(() => state.breadcrumbs),
        extraCrumb: computed(() => state.extraCrumb),
        isLoading: computed(() => state.isLoading),

        // Computed
        allCrumbs,
        lastCrumb,

        // Actions
        updateBreadcrumbs,
        setExtraCrumb,
        clearExtraCrumb,
        clearHistory,
        getBreadcrumbUrl
    };
});
